import React      from "react"
import LineBase   from "./line_base"

import Buttons      from "./shared/buttons"
import Calculations from "./shared/calculations"
import Inputs       from "./shared/inputs"
import Option       from "./shared/option"
import HideInPdf    from "./shared/hide_in_pdf"
import Inactive     from "./line_inactive"
import QuillInput   from '@shared/quillInput'
import Combobox     from '@shared/combobox'

import { getRequest } from '@helpers/javascript/javascript'
import { quoteVatRate } from "@shared/quotes/resolveVatRate"
import { lineNameFor, breakdownNameFor, formatNumber, roundNumber } from "../helpers"

const parseNum = value => {
  const parsed = parseFloat(String(value).replace(",", "."))
  return Number.isFinite(parsed) ? parsed : 0
}

const BreakdownRow = ({ breakdown, lineIndex, breakdownIndex, lineQuantity, currencySymbol, showMargin }) => {
  if (breakdown._destroy) return null

  const quantity  = parseNum(breakdown.quantity)
  const purchase  = parseNum(breakdown.purchase_price)
  const sale      = parseNum(breakdown.unit_price)
  const lineQty   = parseNum(lineQuantity) || 1
  const totalQty  = roundNumber(quantity * lineQty)
  const totalSale = roundNumber(quantity * sale * lineQty)
  const columns   = 4 + (showMargin ? 1 : 0)

  return (
    <React.Fragment>
      <div className="row-inputs service-breakdown" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)`, marginBottom: '12px' }}>
        <div>
          <label>{I18n.t("quotes.new.service_qty_unit")}</label>
          <input className="form-control text-right" value={breakdown.quantity ?? ""} disabled readOnly />
        </div>

        <div>
          <label>{I18n.t("quotes.new.service_qty_total")}</label>
          <input className="form-control text-right" value={formatNumber(totalQty)} disabled readOnly />
        </div>

        <div>
          <label>{I18n.t("quotes.bigcard.unit")}</label>
          <input className="form-control text-center" value={breakdown.unit || ""} disabled readOnly />
        </div>

        { showMargin &&
          <div>
            <label>{I18n.t("quotes.new.service_purchase_price")}</label>
            <input className="form-control text-right" value={formatNumber(purchase)} disabled readOnly />
          </div>
        }

        <div>
          <label>{I18n.t("quotes.new.service_unit_price")}</label>
          <input className="form-control text-right" value={formatNumber(sale)} disabled readOnly />
        </div>
      </div>

      <div className="breakdown-total" style={{ width: '20%', paddingLeft: '20px', marginBottom: '12px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', fontSize: '15px' }}>
        <div className="calculation" style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>Total</div>
          <div>{formatNumber(totalSale)} {currencySymbol || ""}</div>
        </div>
      </div>

      <input type="hidden" readOnly name={breakdownNameFor("id",             lineIndex, breakdownIndex)} value={breakdown.id || ""} />
      <input type="hidden" readOnly name={breakdownNameFor("cost_category",  lineIndex, breakdownIndex)} value={breakdown.cost_category || ""} />
      <input type="hidden" readOnly name={breakdownNameFor("quantity",       lineIndex, breakdownIndex)} value={breakdown.quantity ?? ""} />
      <input type="hidden" readOnly name={breakdownNameFor("unit",           lineIndex, breakdownIndex)} value={breakdown.unit || ""} />
      <input type="hidden" readOnly name={breakdownNameFor("purchase_price", lineIndex, breakdownIndex)} value={breakdown.purchase_price || ""} />
      <input type="hidden" readOnly name={breakdownNameFor("unit_price",     lineIndex, breakdownIndex)} value={breakdown.unit_price || ""} />
      <input type="hidden" readOnly name={breakdownNameFor("order",          lineIndex, breakdownIndex)} value={breakdownIndex + 1} />
    </React.Fragment>
  )
}

export default class LineBpu extends LineBase {
  constructor(props) {
    super(props)
    this.state = { ...this.state, options: [], loadingOptions: true }

    this.selectBpuLine     = this.selectBpuLine.bind(this)
    this.changeDescription = this.changeDescription.bind(this)
    this.reset             = this.reset.bind(this)
  }

  componentDidMount() {
    this.fetchOptions()
  }

  componentDidUpdate(prevProps) {
    if (prevProps.quote.mbc_contract?.id !== this.props.quote.mbc_contract?.id) {
      this.fetchOptions()
    }
  }

  fetchOptions() {
    const contractId = this.props.quote.mbc_contract?.id
    if (!contractId) {
      this.setState({ options: [], loadingOptions: false })
      return
    }

    this.setState({ loadingOptions: true })
    getRequest(`/mbc_contracts/${contractId}/mbc_lines.json`)
      .then(options => this.setState({ options: options || [], loadingOptions: false }))
      .catch(error => {
        console.error('Failed to load BPU lines', error)
        this.setState({ options: [], loadingOptions: false })
      })
  }

  selectBpuLine(bpuLineId) {
    const { index, qline } = this.props
    const { replaceLine }  = this.props.buttonProps

    if (!bpuLineId) {
      replaceLine({ ...qline, mbc_line: null, qline_breakdowns: [] }, index)
      return
    }

    const bpuLine = this.state.options.find(o => String(o.id) === String(bpuLineId))
    if (!bpuLine) return

    let vat_rate = qline.vat_rate
    const quoteRate = quoteVatRate(this.props.quote)
    if (quoteRate) vat_rate = quoteRate

    const qlineBreakdowns = (bpuLine.breakdowns || []).map(b => ({
      cost_category:  b.cost_category,
      unit:           b.unit,
      quantity:       b.quantity,
      unit_price:     b.unit_price,
      purchase_price: b.purchase_price,
      order:          b.order
    }))

    replaceLine({
      ...qline,
      mbc_line:            bpuLine,
      description:         bpuLine.description_for_line || bpuLine.label,
      ref_product_distrib: bpuLine.code,
      unit:                bpuLine.unit,
      unit_price:          bpuLine.unit_price,
      purchase_price:      bpuLine.purchase_price,
      vat_rate,
      qline_breakdowns:    qlineBreakdowns
    }, index)
  }

  reset() {
    const { index, qline } = this.props
    const { replaceLine }  = this.props.buttonProps
    replaceLine({ ...qline, mbc_line_id: null, mbc_line: null, qline_breakdowns: [] }, index)
  }

  changeDescription(description) {
    const { index, qline } = this.props
    const { replaceLine }  = this.props.buttonProps
    replaceLine({ ...qline, description }, index)
  }

  render() {
    const { index, quote, qline, vats, showCode, showMargin, buttonProps } = this.props
    const { replaceLine } = this.props.buttonProps
    const { options, loadingOptions } = this.state

    const selectedId    = qline.mbc_line?.id
    const breakdowns    = qline.qline_breakdowns || []
    const hasBreakdowns = breakdowns.some(breakdown => !breakdown._destroy)

    const sectionLabelStyle = {
      fontSize: '0.85em',
      color: '#374151',
      fontWeight: 600,
      marginBottom: '4px',
      marginTop: '8px'
    }

    return (
      qline.active
      ? <div className="quotes--form-qline-container">
          <React.Fragment>
            <div className="quotes--form-full-row">
              <label>{I18n.t("mbc_contracts.mbc_lines.short_label")}</label>
              { selectedId
                ? <div onClick={this.reset}>
                    <input
                      className="form-control mb-3"
                      defaultValue={qline.ref_product_distrib + " — " + (qline.mbc_line?.label || qline.description)}
                      disabled
                    />
                    <input
                      type="hidden"
                      name={lineNameFor("mbc_line_id", index)}
                      defaultValue={selectedId}
                    />
                  </div>
                : <Combobox
                    options         = {options.map(o => ({
                      id:   o.id,
                      name: `${o.favorite ? "★ " : ""}${o.code} — ${o.label}`,
                      ...o
                    }))}
                    value           = {""}
                    prompt          = {loadingOptions ? "…" : I18n.t("mbc_contracts.workorders.bpu_picker")}
                    placeholder     = {I18n.t("mbc_contracts.workorders.bpu_picker")}
                    onSelectElement = {selected => this.selectBpuLine(selected?.id)}
                    className       = "mb-3"
                    required
                    suggestionIcon
                  />
              }
            </div>

            <div className="text-input">
              <QuillInput
                text={qline.description}
                name={lineNameFor('description', index)}
                onChange={this.changeDescription}
              />
            </div>

            <Calculations
              qline={qline}
              index={index}
              replaceLine={replaceLine}
              showMargin={showMargin}
              showPriceCoefficient={quote.allow_markup && !hasBreakdowns}
            />

            <Inputs
              index={index}
              quote={quote}
              qline={qline}
              replaceLine={replaceLine}
              vats={vats}
              showMargin={showMargin}
              readOnlyPrices={hasBreakdowns}
            />

            <div className="checkboxes">
              <Option
                index={index}
                qline={qline}
                replaceLine={replaceLine}
              />

              <HideInPdf
                index={index}
                qline={qline}
                replaceLine={replaceLine}
              />
            </div>

            { breakdowns.map((breakdown, breakdownIndex) =>
              <React.Fragment key={breakdownIndex}>
                <div className="service-kind-header" style={{ ...sectionLabelStyle, width: '80%' }}>
                  {I18n.t(`quotes.new.cost_category.${breakdown.cost_category || "materials"}`)}
                </div>
                <BreakdownRow
                  breakdown      = {breakdown}
                  lineIndex      = {index}
                  breakdownIndex = {breakdownIndex}
                  lineQuantity   = {qline.quantity}
                  currencySymbol = {qline.currency_symbol}
                  showMargin     = {showMargin}
                />
              </React.Fragment>
            )}

            <Buttons
              index={index}
              qline={qline}
              buttonProps={buttonProps}
            />

            <input
              type="hidden"
              name={lineNameFor("id", index)}
              defaultValue={qline.id}
            />

            <input
              type="hidden"
              name={lineNameFor("order", index)}
              defaultValue={index + 1}
            />
          </React.Fragment>
        </div>
      : <Inactive
          index={index}
          qline={qline}
          showCode={showCode}
          buttonProps={buttonProps}
        />
    )
  }
}
